/**
 * Connect your AI's hooks: the clock, the connect a panel started, and
 * where `/setup`'s connect is.
 */
import { useEffect, useState } from "react";
import type { AiProvider, Me } from "../../app/me";
import { useConnectAi } from "./api";
import { connectFailure, initialProvider, startedAccount, type StartedConnect } from "./onboarding";
import { connectProvider, setupView, shownProvider } from "./setup-view";

type Ai = NonNullable<Me["computer"]>["ai"];

export type Connect = ReturnType<typeof useConnectAi>;

/** The time now, a tick a second: for a clock on screen. */
export function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(tick);
  }, []);
  return now;
}

/** The connect this panel started, if it did: its provider, and the account it's for. */
export function startedConnect(connect: Connect): StartedConnect | undefined {
  if (!connect.isSuccess) return undefined;
  const ai = connect.data.ai;
  return { provider: connect.variables.provider, account: ai ? startedAccount(ai) : undefined };
}

/**
 * Where Connect your AI is, and the provider it's about (`setupView`). A
 * failed connect shows why until the owner goes back to the form.
 */
export function useSetupConnect(ai: Ai | null, done: boolean) {
  const connect = useConnectAi();
  const started = startedConnect(connect);
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState<AiProvider | null>(() =>
    ai ? initialProvider(ai, undefined) : null,
  );
  // Adjusted as it renders: a new connect's failure, if it fails, shows again.
  if (ai?.status === "connecting" && editing) setEditing(false);

  const provider = connectProvider(ai, started, chosen);
  const view = setupView(ai, { done, editing, provider });
  const pick = (p: AiProvider | null, edit: boolean) => {
    setChosen(p);
    setEditing(edit);
    connect.reset();
  };
  return {
    connect,
    view,
    provider: shownProvider(view, chosen, provider),
    failure: failureOf(ai, started),
    choose: (p: AiProvider | null) => pick(p, editing),
    edit: (p: AiProvider | null) => pick(p, true),
  };
}

export type SetupConnectState = ReturnType<typeof useSetupConnect>;

/** Why the connect failed: the account's reason, else the workspace's. */
function failureOf(ai: Ai | null, started: StartedConnect | undefined): string | null {
  return ai && (connectFailure(ai, started) ?? ai.error ?? null);
}
